import React, { createContext, useContext, useState } from 'react';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';

const AlertContext = createContext();

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within AlertProvider');
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    const [alert, setAlert] = useState(null);

    const showAlert = (message, options = {}) => {
        return new Promise((resolve) => {
            setAlert({
                type: options.type || 'info', // 'info', 'success', 'error', 'warning'
                title: options.title || 'Aviso',
                message,
                onClose: () => {
                    setAlert(null);
                    resolve(true);
                }
            });
        });
    };

    const showConfirm = (message, options = {}) => {
        return new Promise((resolve) => {
            setAlert({
                type: 'confirm',
                title: options.title || 'Confirmar',
                message,
                confirmText: options.confirmText || 'Aceptar',
                cancelText: options.cancelText || 'Cancelar',
                onConfirm: () => {
                    setAlert(null);
                    resolve(true);
                },
                onCancel: () => {
                    setAlert(null);
                    resolve(false);
                }
            });
        });
    };

    return (
        <AlertContext.Provider value={{ showAlert, showConfirm }}>
            {children}
            {alert && <AlertModal alert={alert} />}
        </AlertContext.Provider>
    );
};

const AlertModal = ({ alert }) => {
    const getIcon = () => {
        switch (alert.type) {
            case 'success':
                return <CheckCircle2 size={48} className="text-green-500" />;
            case 'error':
                return <AlertCircle size={48} className="text-red-500" />;
            case 'warning':
                return <AlertCircle size={48} className="text-yellow-500" />;
            case 'confirm':
                return <AlertCircle size={48} className="text-blue-500" />;
            default:
                return <AlertCircle size={48} className="text-neutral-500" />;
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && alert.type !== 'confirm') {
            alert.onClose?.();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100000001] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in"
            onClick={handleBackdropClick}
        >
            <div className="bg-neutral-900 border-2 border-neutral-800 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-neutral-800 flex items-center justify-between">
                    <h3 className="text-lg font-black text-white uppercase tracking-wider">
                        {alert.title}
                    </h3>
                    {alert.type !== 'confirm' && (
                        <button
                            onClick={alert.onClose}
                            className="text-neutral-500 hover:text-white transition-colors p-1"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-4">
                    <div className="flex flex-col items-center gap-4 text-center">
                        {getIcon()}
                        <p className="text-neutral-300 whitespace-pre-line">
                            {alert.message}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-neutral-800 flex gap-3">
                    {alert.type === 'confirm' ? (
                        <>
                            <button
                                id="confirm-modal-cancel-btn"
                                onClick={alert.onCancel}
                                className="flex-1 py-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg font-bold uppercase tracking-wider transition-colors"
                            >
                                {alert.cancelText}
                            </button>
                            <button
                                id="confirm-modal-accept-btn"
                                onClick={alert.onConfirm}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold uppercase tracking-wider transition-colors"
                            >
                                {alert.confirmText}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={alert.onClose}
                            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold uppercase tracking-wider transition-colors"
                        >
                            Entendido
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
